import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, largestClassGroup, treatsSeveral } from '../skillUsage';

export const wordStaggerEchoSkill: CodeoSkill = {
    name: SKILL_NAMES.wordStaggerEcho,
    kind: 'codeo',
    description:
        'Stack one word in four or five identical rows that fill the frame and cascade in, row ' +
        'after row and letter after letter, one row in the accent colour — repetition you can ' +
        'see, for a word about repeating, momentum, multiplying, scale or "again and again".',
    checkLooksFor:
        'three or more rows of the same word, each masked (overflow: hidden), their letters tweened on yPercent row after row',
    documentUsesSkill: check(
        (script, markup) =>
            /overflow:\s*hidden|\boverflow-hidden\b/.test(markup) &&
            /yPercent/.test(script) &&
            treatsSeveral(script) &&
            (largestClassGroup(markup, 'div') >= 3 || /cloneNode|createElement/.test(script)),
    ),
    instructions: `# Word Stagger Echo

One word, said once, shown three to five times. The rows stack to fill the frame top to bottom, each the same width, and they **cascade in**: every row's letters rise out of their own mask one after another, and every row starts a little after the row above, so the build runs diagonally from top-left to bottom-right like a wave. One row — usually the second — is in the accent colour. The repetition is the meaning: more, again, every time, momentum, at scale.

Use it on a word about repetition or accumulation: repeat, again, every, more, scale, keep, stagger, multiply. It takes the whole frame, so it is a beat on its own.

${KINETIC_WORD_SETTING}

## Markup

Build the rows from one template in script so they are identical. Each row is a mask (\`overflow: hidden\`) with letter spans carrying \`data-l5-allow-clip\`.

\`\`\`html
<div id="echo" class="absolute inset-0 flex flex-col items-center justify-center" style="gap: 0;"></div>
\`\`\`

\`\`\`css
.echo-row { flex: none; overflow: hidden; line-height: 1; padding: 0.02em 0.04em 0.06em; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa); }
.echo-row.accent { color: var(--l5-color-1, #ff5a36); }
.echo-ch { display: inline-block; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const WORD = 'AGAIN', ACCENT_ROW = 1;
const FRAME_H = 1080;
const echo = document.querySelector('#echo');
function makeRow(r) {
  const row = document.createElement('div');
  row.className = 'echo-row' + (r === ACCENT_ROW ? ' accent' : '');
  row.setAttribute('data-l5-allow-literal', '');
  if (r > 0) row.setAttribute('aria-hidden', 'true');
  row.innerHTML = [...WORD].map((c) => '<span class="echo-ch" data-l5-allow-clip>' + c + '</span>').join('');
  echo.appendChild(row);
  return row;
}
// How many rows fit when each fills the width decides the count: as many as fit, 3 to 5. A short
// word on a wide frame gets 3 big rows; a tall frame gets 5. Only past that does the type shrink.
// Every row takes the first row's size again each time it is refitted.
const first = makeRow(0);
const rows = [first];
fitWord(first, 0.86, (size) => {
  if (rows.length === 1) {
    const count = Math.max(3, Math.min(5, Math.floor((FRAME_H * 0.94) / first.offsetHeight)));
    for (let r = 1; r < count; r++) rows.push(makeRow(r));
  }
  rows.forEach((row) => { row.style.fontSize = size; });
  const tooTall = (rows.length * first.offsetHeight) / (FRAME_H * 0.94);
  if (tooTall > 1) rows.forEach((row) => { row.style.fontSize = parseFloat(size) / tooTall + 'px'; });
});

const CUE = 0.35;
const ROW_GAP = 0.033, LETTER_GAP = 0.021;
rows.forEach((row, r) => {
  row.querySelectorAll('.echo-ch').forEach((ch, i) => {
    tl.fromTo(ch,
      { yPercent: 160, scaleY: 1.35, transformOrigin: '50% 100%' },
      { yPercent: 0, scaleY: 1, duration: 0.2, ease: 'power3.out' },
      CUE - 0.08 + r * ROW_GAP + i * LETTER_GAP);
  });
});
tl.set({}, {}, 1.8);
window.__timelines = { main: tl };
\`\`\`

(\`fitWord\` is the helper from "Setting the word".)

## Adapt

- **The row count follows the frame.** Each row fills the width first; the count is however many such rows fit the height, three to five. Fixing the count at five on a wide frame shrinks every row to a third of the width.
- **Exit as a wave too.** Drop the rows out in the same diagonal order, or let the whole stack slide off the top as one block.
- **Build to a payoff.** For a sentence that ends on the repeated word, start with one row and add a row on each repetition in the narration.

## Pitfalls

- **Same word, same size.** Every row is identical; the accent row differs only in colour. A different word or size on one row is a list, not an echo.
- **The cascade is quick.** The last letter of the last row lands within about 0.35s of the first. Slowed down, it becomes a list being read out.
- **Rows touch.** No gap between rows: the stack reads as one block of type.
- **Rows never shrink.** Each row is a flex item with \`overflow: hidden\`, and such an item shrinks to fit its column, cropping its own letters, unless it is \`flex: none\`. The height check measures the rows, so a shrunk row also hides the problem from it.
- **Each row is a mask.** \`overflow: hidden\` on every row is what hides the parked letters; set it visible to quiet a clipping warning and every row is on screen before it arrives. The letters' \`data-l5-allow-clip\` is what answers that check, and the rows' padding stays a hair (0.02–0.06em).
- The letters are transform-tweened, so they carry no CSS \`transform\`.`,
};
